import React, { Component } from 'react';
import { FjInput, FjStyles, FjButton } from 'components/primitives';
import classnames from 'classnames';

import AuthContainer from './auth-container';

const MIN_PASSWORD_LENGTH = 8;

const buttonStyles = {
  color: FjStyles.colors.teal.hex(),
  background: FjStyles.colors.authGray.hex(),
  borderColor: FjStyles.colors.authGray.hex(),
  textDecoration: 'underline',
};

interface Notification {
  type?: string;
  message?: string;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface FjAuthComponentProps {
  renderHeader: () => React.ReactNode;
  forgotPasswordHref: string;
  createAccountHref: string;
  learnMoreHref: string;
  onSignIn: (credentials: SignInCredentials) => void;
  notification?: Notification;
}

interface FjAuthComponentState {
  email: string;
  password: string;
}

class FjAuthComponent extends Component<FjAuthComponentProps, FjAuthComponentState> {
  static defaultProps = {
    notification: {},
  };

  constructor(props: FjAuthComponentProps) {
    super(props);

    this.state = {
      email: '',
      password: '',
    };
  }

  onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    this.props.onSignIn({
      email: this.state.email,
      password: this.state.password,
    });
    this.clearPasswordField();
  };

  onValueChange = (key: keyof FjAuthComponentState, value: string): void => {
    this.setState({
      [key]: value,
    } as Pick<FjAuthComponentState, keyof FjAuthComponentState>);
  };

  validatePassword = (val: string): boolean =>
    val.length >= MIN_PASSWORD_LENGTH || val.length === 0;

  validate = (): boolean => {
    if (this.state.password.length < MIN_PASSWORD_LENGTH || !this.state.email.length) {
      return false;
    }
    return true;
  };

  clearPasswordField = (): void => this.setState({ password: '' });

  renderNotification = (): React.ReactNode => (
    <div
      className={classnames(
        'notification',
        this.props.notification!.type === 'ERROR' ? 'error' : 'message',
      )}
      data-test={`auth-notification-${this.props.notification!.type!.toLowerCase()}`}
    >
      {this.props.notification!.message}
    </div>
  );

  render(): React.ReactNode {
    const { learnMoreHref } = this.props;
    return (
      <AuthContainer>
        {this.props.renderHeader()}
        <form onSubmit={this.onSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <FjInput
            className="fj-input"
            value={this.state.email}
            placeholder="email"
            autoComplete="username"
            onChange={(val: string) => this.onValueChange('email', val)}
            borderColor={FjStyles.colors.teal.hex()}
            height={40}
            fontSize={14}
          />
          <FjInput
            type="password"
            className="fj-input"
            value={this.state.password}
            placeholder="password"
            autoComplete="current-password"
            validator={this.validatePassword}
            getValidationMessage={() => 'Passwords must be 8 characters or longer.'}
            onChange={(val: string) => this.onValueChange('password', val)}
            borderColor={FjStyles.colors.teal.hex()}
            height={40}
            fontSize={14}
          />
          <FjButton
            className="fj-link forgot-password"
            href={this.props.forgotPasswordHref}
            defaultStyle={buttonStyles}
            hoverStyle={buttonStyles}
            height={20}
            width={50}
          >
            Forgot Password?
          </FjButton>
          {this.props.notification && this.props.notification.message && this.renderNotification()}
          <FjButton
            type="submit"
            className="fj-button"
            defaultStyle={{
              background: FjStyles.colors.white.hex(),
              color: FjStyles.colors.teal.hex(),
              borderColor: FjStyles.colors.teal.hex(),
            }}
            hoverStyle={{
              background: FjStyles.colors.teal.hex(),
              color: FjStyles.colors.white.hex(),
              borderColor: FjStyles.colors.teal.hex(),
            }}
            disabled={!this.validate()}
            data-test="submit"
          >
            Sign In
          </FjButton>
        </form>
        <div className="footer">
          <FjButton
            className="fj-link"
            href={this.props.createAccountHref}
            defaultStyle={buttonStyles}
            hoverStyle={buttonStyles}
            height={20}
            width={50}
          >
            Create Account
          </FjButton>
          <FjButton
            className="fj-link"
            href={learnMoreHref}
            defaultStyle={buttonStyles}
            hoverStyle={buttonStyles}
            height={20}
            width={50}
          >
            www.flowjo.com
          </FjButton>
        </div>
      </AuthContainer>
    );
  }
}

export default FjAuthComponent;
